import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  FormControlLabel,
  Checkbox,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createPropertyEnquiry,
  getPropertyById,
  getPlotById,
} from "../../Services/ApiServices";
import EMICalculator from "./Propertyguide/EMICalculator";
import Navbar from "../../Components/Navbar";
import Footer from "../Home/Footer";

const sectionTabs = [
  "Overview",
  "Floor Plan",
  "Amenities",
  "Gallery",
  "EMI Calculator",
];

const cleanReraIdValueOnly = (reraId) => {
  if (!reraId) return "N/A";
  let cleaned = reraId.trim();
  cleaned = cleaned.replace(/^(PROJECT RERA ID:|RERA ID:)\s*/i, "");
  return cleaned;
};

const PropertyDetails = () => {
  // Callback dialog state
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [selectedFloorPlan, setSelectedFloorPlan] = useState(null);
  const [callbackForm, setCallbackForm] = useState({
    name: "",
    phone: "",
    countryCode: "+91",
    consent: false,
  });
  const [callbackError, setCallbackError] = useState("");
  const [callbackSuccess, setCallbackSuccess] = useState("");

  const handleCallbackOpen = (floorPlan) => {
    setSelectedFloorPlan(floorPlan);
    setCallbackForm({
      name: "",
      phone: "",
      countryCode: "+91",
      consent: false,
    });
    setCallbackError("");
    setCallbackSuccess("");
    setCallbackOpen(true);
  };

  const handleCallbackClose = () => {
    setCallbackOpen(false);
    setSelectedFloorPlan(null);
    setCallbackError("");
    setCallbackSuccess("");
  };

  const handleCallbackChange = (e) => {
    const { name, value } = e.target;
    setCallbackForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (callbackError) setCallbackError("");
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();

    if (!/^\d{10}$/.test(callbackForm.phone)) {
      setCallbackError("Please enter a valid 10-digit phone number");
      return;
    }

    if (!callbackForm.consent) {
      setCallbackError("Please agree to be contacted");
      return;
    }

    try {
      const payload = {
        name: callbackForm.name,
        phoneNumber: callbackForm.phone,
        propertyId: propertyData?.id,
        allowContact: true,
      };

      await createPropertyEnquiry(payload);
      setCallbackSuccess(
        "Thank you! Our representative will contact you soon.",
      );

      setTimeout(() => {
        handleCallbackClose();
      }, 2000);
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      setCallbackError("Failed to submit enquiry. Please try again.");
    }
  };

  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("Overview");
  const [propertyData, setPropertyData] = useState(null);
  const [isUnavailable, setIsUnavailable] = useState(false);

  // Gallery modal state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIdx, setGalleryIdx] = useState(0);

  // Brochure modal state
  const [brochureModalOpen, setBrochureModalOpen] = useState(false);

  // Enquiry form state
  const [enquiryForm, setEnquiryForm] = useState({
    name: "",
    mobile: "",
    agree: false,
  });
  const [enquirySuccess, setEnquirySuccess] = useState("");
  const [enquiryError, setEnquiryError] = useState("");

  // Brochure form state
  const [brochureForm, setBrochureForm] = useState({
    name: "",
    mobile: "",
    email: "",
    agree: false,
  });
  const [brochureSuccess, setBrochureSuccess] = useState("");
  const [brochureError, setBrochureError] = useState("");

  // Gallery and UI state
  const [isLoading, setIsLoading] = useState(true);
  const [videoOpen, setVideoOpen] = useState(false);
  const [activeBhkTab, setActiveBhkTab] = useState("");
  const [activeGalleryTab, setActiveGalleryTab] = useState("");
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(true);

  // Get unique gallery types and include Videos tab if video exists
  const galleryTypes = React.useMemo(() => {
    const types = [];

    if (propertyData?.youtube_link) {
      types.push({ type: "video", label: "Videos" });
    }

    if (propertyData?.galleries?.length > 0) {
      const galleryTypes = new Set();
      propertyData.galleries.forEach((item) => {
        if (item.galleryType) {
          galleryTypes.add(item.galleryType);
        }
      });

      if (galleryTypes.size === 0) {
        types.push({ type: "images", label: "Images" });
      } else {
        Array.from(galleryTypes).forEach((type) => {
          types.push({
            type,
            label: type.charAt(0).toUpperCase() + type.slice(1),
          });
        });
      }
    }

    if (types.length === 0) {
      types.push({ type: "images", label: "Gallery" });
    }

    return types;
  }, [propertyData]);

  // Filter gallery items based on active tab
  const filteredGalleryItems = React.useMemo(() => {
    if (activeGalleryTab === "video") {
      return [];
    }

    if (!propertyData?.galleries?.length) return [];

    if (activeGalleryTab === "images") {
      return propertyData.galleries;
    }

    return propertyData.galleries.filter(
      (item) => item.galleryType === activeGalleryTab,
    );
  }, [propertyData, activeGalleryTab]);

  // Set the first available tab when galleryTypes changes
  useEffect(() => {
    if (
      galleryTypes.length > 0 &&
      !galleryTypes.some((tab) => tab.type === activeGalleryTab)
    ) {
      setActiveGalleryTab(galleryTypes[0].type);
    }
  }, [galleryTypes, activeGalleryTab]);

  // Get unique BHK types from floor plans
  const bhkTypes =
    propertyData?.floorplans?.reduce((acc, plan) => {
      if (plan.bhkType && !acc.includes(plan.bhkType)) {
        acc.push(plan.bhkType);
      }
      return acc;
    }, []) || [];

  React.useEffect(() => {
    if (bhkTypes && bhkTypes.length > 0 && !activeBhkTab) {
      setActiveBhkTab(bhkTypes[0]);
    }
  }, [bhkTypes, activeBhkTab]);

  // Filter floor plans based on active BHK tab
  const filteredFloorPlans =
    propertyData?.floorplans?.filter((plan) => plan.bhkType === activeBhkTab) ||
    [];

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === "") return "";
    const numPrice = Number(price);
    if (isNaN(numPrice)) return price;
    if (numPrice >= 10000000)
      return `₹ ${(numPrice / 10000000).toFixed(2).replace(/\.00$/, "")} Cr`;
    if (numPrice >= 100000)
      return `₹ ${(numPrice / 100000).toFixed(2).replace(/\.00$/, "")} L`;
    return `₹ ${numPrice.toLocaleString()}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      const searchParams = new URLSearchParams(location.search);
      const projectId = searchParams.get("project");
      const propertyTypeFromUrl = searchParams.get("type");

      if (!projectId) return;

      setIsLoading(true);
      setIsUnavailable(false);
      try {
        let response;
        if (propertyTypeFromUrl === "plot") {
          response = await getPlotById(projectId);
          if (response && response.status && response.data) {
            if (response.data.isActive === false || response.data.isDeleted) {
              setIsUnavailable(true);
              setPropertyData(null);
            } else {
              setPropertyData({ ...response.data, isPlot: true });
            }
          }
        } else {
          response = await getPropertyById(projectId);
          if (response && response.status && response.property) {
            if (
              response.property.isActive === false ||
              response.property.isDeleted
            ) {
              setIsUnavailable(true);
              setPropertyData(null);
            } else {
              setPropertyData(response.property);
            }
          }
        }
      } catch (error) {
        if (error?.response?.status === 403) {
          setIsUnavailable(true);
          setPropertyData(null);
        } else {
          console.error("Failed to fetch property details:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [location.search]);

  if (isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "white" }}>
        <Navbar />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
          }}
        >
          <CircularProgress
            size={60}
            sx={{
              color: "red",
              "&.MuiCircularProgress-root": {
                animationDuration: "550ms",
              },
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
        </Box>
      </Box>
    );
  }

  if (isUnavailable) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "white" }}>
        <Navbar />
        <Container sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Property unavailable
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            This property is currently inactive and cannot be viewed.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/realestate")}
            sx={{ bgcolor: "#B02F00", "&:hover": { bgcolor: "#8a2500" } }}
          >
            Browse active properties
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  if (!propertyData) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "white" }}>
        <Navbar />
        <Container sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Property not found
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/realestate")}
            sx={{ mt: 2, bgcolor: "#B02F00", "&:hover": { bgcolor: "#8a2500" } }}
          >
            Browse properties
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  // Welcome Modal Component
  const WelcomeModal = () => (
    <Dialog
      open={welcomeModalOpen}
      onClose={() => setWelcomeModalOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          overflow: "hidden",
          position: "relative",
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <Box
          component="img"
          src={welcomeData.image}
          alt="Welcome"
          sx={{
            width: "100%",
            height: "200px",
            objectFit: "cover",
            display: "block",
          }}
        />
        <IconButton
          onClick={() => setWelcomeModalOpen(false)}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 1)",
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{ fontWeight: 600, color: "primary.main", fontFamily: "Manrope" }}
        >
          {welcomeData.title}
        </Typography>
        <Typography
          variant="body1"
          paragraph
          sx={{ fontFamily: "Inter", color: "#4B5563" }}
        >
          {welcomeData.description}
        </Typography>
      </Box>
    </Dialog>
  );

  // Welcome modal data
  const welcomeData = {
    title: "Welcome to Abilio Realty!",
    description:
      "At Abilio Realty, we take pride in being an independent real estate advisory firm with a strong presence. With our vast experience and in-depth knowledge of the real estate landscape, we aspire to be your first choice when it comes to buying residential properties. Our commitment is to provide every client with trustworthy guidance, transparent processes, and exceptional service, ensuring that your journey of owning a home is smooth, secure, and satisfying.",
    image:
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1073&q=80",
  };

  if (!propertyData) return null;

  // Prepare stats for display
  const propertyStats = [
    propertyData.isPlot &&
      propertyData.facing && {
        iconClass: "fa-regular fa-compass",
        label: "Facing",
        value: propertyData.facing,
      },
    propertyData.isPlot &&
      propertyData.dimensions && {
        iconClass: "fa-solid fa-ruler-combined",
        label: "Dimensions",
        value: propertyData.dimensions,
      },
    !propertyData.isPlot &&
      propertyData.propertyBhks?.length > 0 && {
        iconClass: "fa-solid fa-building-flag",
        label: "Apartment Type",
        value: propertyData.propertyBhks
          .map((bhk) => `${bhk.bhkType} BHK`)
          .join(", "),
      },
    (propertyData.minArea || propertyData.maxArea) && {
      iconClass: "fa-solid fa-building",
      label: "Area Range",
      value:
        propertyData.minArea && propertyData.maxArea
          ? `${propertyData.minArea} - ${propertyData.maxArea} sqft`
          : propertyData.minArea
            ? `From ${propertyData.minArea} sqft`
            : `Up to ${propertyData.maxArea} sqft`,
    },
    (propertyData.minPrice || propertyData.maxPrice) && {
      iconClass: "fa-solid fa-building-circle-check",
      label: "Price Range",
      value:
        propertyData.minPrice && propertyData.maxPrice
          ? `${formatPrice(propertyData.minPrice)} - ${formatPrice(propertyData.maxPrice)}`
          : propertyData.minPrice
            ? `From ${formatPrice(propertyData.minPrice)}`
            : `Up to ${formatPrice(propertyData.maxPrice)}`,
    },
  ].filter(Boolean);

  const galleryImages = propertyData.galleries
    ? propertyData.galleries.map((g) => g.mediaLink)
    : [];
  const mainImage = propertyData.isPlot
    ? propertyData.imageUrl
    : propertyData.proertyImage;
  let allImages = [mainImage, ...galleryImages];
  let images = [...new Set(allImages)].filter(Boolean);

  if (propertyData.youtube_link) {
    images = [...images, propertyData.youtube_link];
  }

  const handleGalleryOpen = (idx) => {
    setGalleryIdx(idx);
    setGalleryOpen(true);
  };
  const handleGalleryClose = () => setGalleryOpen(false);
  const handleGalleryPrev = () =>
    setGalleryIdx((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  const handleGalleryNext = () =>
    setGalleryIdx((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  const handleEnquiryChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEnquiryForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setEnquiryError("");
    setEnquirySuccess("");
  };

  const handleBrochureFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBrochureForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setBrochureError("");
    setBrochureSuccess("");
  };

  const handleBrochureModalOpen = () => {
    setBrochureModalOpen(true);
  };

  const handleBrochureModalClose = () => {
    setBrochureModalOpen(false);
    setBrochureForm({ name: "", mobile: "", email: "", agree: false });
    setBrochureError("");
    setBrochureSuccess("");
  };

  const formatDate = (dateString, format = "full") => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (format === "year") {
      return date.getFullYear().toString();
    }
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const overviewFields = [
    propertyData.status && ["Status", propertyData.status],
    propertyData.totalArea && [
      "Total Area",
      `${propertyData.totalArea.toLocaleString("en-IN")} Acres`,
    ],
    propertyData.totalApartments && [
      "Total Units",
      propertyData.totalApartments,
    ],
    propertyData.launchDate && [
      "Launch Year",
      formatDate(propertyData.launchDate, "year"),
    ],
    propertyData.availability && ["Availability", propertyData.availability],
    propertyData.possessionDate && [
      "Possession Date",
      formatDate(propertyData.possessionDate, "year"),
    ],
    propertyData.reraId && [
      "RERA ID",
      cleanReraIdValueOnly(propertyData.reraId),
    ],
    propertyData.propertyType && ["Property Type", propertyData.propertyType],
  ].filter(Boolean);

  const handleViewMap = () => {
    if (propertyData.iframeCodeMap) {
      const urlMatch = propertyData.iframeCodeMap.match(/src=["'](.*?)["']/);
      if (urlMatch && urlMatch[1]) {
        window.open(urlMatch[1], "_blank");
      } else {
        const searchQuery = encodeURIComponent(
          propertyData.iframeCodeMap || "",
        );
        window.open(
          `https://www.google.com/maps/search/?api=1&query=${searchQuery}`,
          "_blank",
        );
      }
    } else if (propertyData.location) {
      const searchQuery = encodeURIComponent(propertyData.location);
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${searchQuery}`,
        "_blank",
      );
    }
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setEnquiryError("");
    setEnquirySuccess("");

    if (!enquiryForm.name.trim()) {
      setEnquiryError("Please enter your name.");
      return;
    }
    if (!/^\d{10}$/.test(enquiryForm.mobile)) {
      setEnquiryError("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!enquiryForm.agree) {
      setEnquiryError("You must agree to be contacted.");
      return;
    }

    const payload = {
      name: enquiryForm.name,
      phoneNumber: enquiryForm.mobile,
      propertyId: propertyData.id,
      allowContact: enquiryForm.agree,
    };

    try {
      await createPropertyEnquiry(payload);
      setEnquirySuccess("Thank you! Our representative will contact you soon.");
      setEnquiryForm({ name: "", mobile: "", agree: false });
    } catch (err) {
      setEnquiryError("Failed to submit enquiry. Please try again.");
    }
  };

  const handleBrochureSubmit = async (e) => {
    e.preventDefault();
    setBrochureError("");
    setBrochureSuccess("");

    if (!brochureForm.name.trim()) {
      setBrochureError("Please enter your name.");
      return;
    }
    if (!/^\d{10}$/.test(brochureForm.mobile)) {
      setBrochureError("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (
      !brochureForm.email.trim() ||
      !/\S+@\S+\.\S+/.test(brochureForm.email)
    ) {
      setBrochureError("Please enter a valid email address.");
      return;
    }
    if (!brochureForm.agree) {
      setBrochureError("You must agree to be contacted.");
      return;
    }

    const payload = {
      name: brochureForm.name,
      phoneNumber: brochureForm.mobile,
      email: brochureForm.email,
      propertyId: propertyData.id,
      allowContact: brochureForm.agree,
    };

    try {
      await createPropertyEnquiry(payload);
      setBrochureSuccess("Brochure download initiated! Check your email.");

      setTimeout(() => {
        handleDownloadBrochure();
        handleBrochureModalClose();
      }, 1000);
    } catch (err) {
      setBrochureError("Failed to submit request. Please try again.");
    }
  };

  const handleDownloadBrochure = () => {
    if (!propertyData.brochure) return;
    const viewerUrl = `/pdf-viewer?url=${encodeURIComponent(propertyData.brochure)}&property=${encodeURIComponent(propertyData.name)}`;
    window.open(viewerUrl, "_blank");
  };

  const hasVideo =
    propertyData?.youtube_link &&
    (propertyData.youtube_link.includes("youtube.com") ||
      propertyData.youtube_link.includes("youtu.be")) &&
    propertyData.youtube_link !== "null" &&
    propertyData.youtube_link !== "undefined" &&
    propertyData.youtube_link.trim() !== "";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "white", textAlign: "left" }}>
      <Navbar />
      <WelcomeModal />

      <Container maxWidth="xl" sx={{ py: 2, mt: 3, pb: 8 }}>
        {/* Top Header Section (Spans full width above columns) */}
        <Box sx={{ mb: 3 }}>
          {/* Title and Builder */}
          <Typography
            variant="h2"
            sx={{
              fontFamily: "Manrope",
              fontWeight: 700,
              fontSize: { xs: "28px", md: "36px" },
              color: "#1C1917",
              mb: 1,
            }}
          >
            {propertyData.name}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <i
              style={{ color: "#FF5722" }}
              className="fa-solid fa-location-dot"
            ></i>
            <Typography
              variant="subtitle1"
              sx={{ fontFamily: "Inter", fontSize: "15px", color: "#4B5563" }}
            >
              By{" "}
              <span style={{ color: "#FF5722", fontWeight: 600 }}>
                {propertyData.groupName}
              </span>
            </Typography>
          </Box>

          {/* Location and Map */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                display: "flex",
                alignItems: "center",
                fontFamily: "Inter",
                fontSize: "15px",
                color: "#4B5563",
              }}
            >
              {propertyData.location}
              {!propertyData.isPlot && (
                <Typography
                  component="span"
                  variant="body1"
                  onClick={handleViewMap}
                  sx={{
                    color: "#FF5722",
                    ml: 1.5,
                    cursor: "pointer",
                    fontWeight: 600,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  View on Map
                </Typography>
              )}
            </Typography>
          </Box>
        </Box>

        {/* Dynamic Dual Column Layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
          }}
        >
          {/* Left Section */}
          <Box sx={{ flex: { md: 2 } }}>
            {/* Main Image Slider Container (Now aligns perfectly equal with right sidebar top) */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <Box
                component="img"
                src={propertyData.imageUrl || propertyData.proertyImage}
                alt={propertyData.name}
                sx={{
                  width: "100%",
                  height: { xs: "250px", sm: "400px", md: "480px" },
                  objectFit: "cover",
                  cursor: "pointer",
                }}
                onClick={() => handleGalleryOpen(0)}
              />
              <IconButton
                sx={{
                  position: "absolute",
                  right: 15,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  color: "#1C1917",
                  "&:hover": { backgroundColor: "white" },
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleGalleryOpen(0);
                }}
              >
                <ChevronRightIcon />
              </IconButton>
              <IconButton
                sx={{
                  position: "absolute",
                  left: 15,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  color: "#1C1917",
                  "&:hover": { backgroundColor: "white" },
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleGalleryOpen(0);
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Box>

            {/* RERA ID Badge (Placed beautifully right below the main image container) */}
            {propertyData.reraId && (
              <Typography
                variant="body1"
                sx={{
                  mt: 3,
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "15px",
                  color: "#1F2937",
                }}
              >
                PROJECT RERA ID: {cleanReraIdValueOnly(propertyData.reraId)}
              </Typography>
            )}

            {/* Stat Cards */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {propertyStats.map((item, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Paper
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      borderRadius: 2,
                      border: "1px solid #E5E7EB",
                      p: 2,
                      bgcolor: "white",
                    }}
                    elevation={0}
                  >
                    <i
                      className={item.iconClass}
                      style={{ fontSize: 24, color: "#FF5722", minWidth: 30 }}
                    ></i>
                    <Box>
                      {item.label && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: "#6B7280",
                            fontFamily: "Inter",
                            fontWeight: 500,
                          }}
                        >
                          {item.label}
                        </Typography>
                      )}
                      {item.value && (
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            fontSize: "15px",
                            color: "#111827",
                            fontFamily: "Inter",
                          }}
                        >
                          {item.value}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Video and Brochure Section */}
            {(hasVideo || propertyData.brochure) && (
              <Box sx={{ mt: 4, mb: 4 }}>
                {hasVideo && (
                  <Typography
                    variant="h6"
                    sx={{ fontFamily: "Manrope", fontWeight: 700, mb: 2 }}
                  >
                    Video Tour
                  </Typography>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 3,
                    alignItems: { xs: "stretch", md: "center" },
                  }}
                >
                  {/* Video Thumbnail */}
                  {hasVideo && (
                    <Box
                      onClick={() => setVideoOpen(true)}
                      sx={{
                        position: "relative",
                        width: { xs: "100%", md: "220px" },
                        height: { xs: "200px", md: "130px" },
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        cursor: "pointer",
                        flexShrink: 0,
                        transition: "transform 0.2s",
                        "&:hover": { transform: "scale(1.02)" },
                      }}
                    >
                      <Box
                        component="img"
                        src={`https://img.youtube.com/vi/${
                          propertyData.youtube_link.includes("youtu.be/")
                            ? propertyData.youtube_link
                                .split("youtu.be/")[1]
                                ?.split("?")[0] || ""
                            : propertyData.youtube_link
                                .split("v=")[1]
                                ?.split("&")[0] || ""
                        }/hqdefault.jpg`}
                        alt="Video Thumbnail"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/220x140?text=No+Video";
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "44px",
                          height: "44px",
                          backgroundColor: "rgba(255, 87, 34, 0.9)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                          "&:before": {
                            content: '""',
                            width: 0,
                            height: 0,
                            borderTop: "8px solid transparent",
                            borderBottom: "8px solid transparent",
                            borderLeft: "14px solid white",
                            marginLeft: "4px",
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Download Brochure Button */}
                  {propertyData.brochure && (
                    <Box sx={{ alignSelf: { xs: "stretch", md: "center" } }}>
                      <Button
                        variant="contained"
                        onClick={handleBrochureModalOpen}
                        startIcon={<i className="fas fa-download"></i>}
                        sx={{
                          whiteSpace: "nowrap",
                          width: { xs: "100%", md: "auto" },
                          bgcolor: "#FF5722",
                          fontFamily: "Inter",
                          fontWeight: 600,
                          fontSize: "14px",
                          textTransform: "none",
                          px: 3,
                          py: 1.2,
                          borderRadius: 2,
                          "&:hover": { bgcolor: "#E64A19" },
                        }}
                      >
                        DOWNLOAD BROCHURE
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Video Dialog */}
            <Dialog
              open={videoOpen}
              onClose={() => setVideoOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <Box
                sx={{
                  position: "relative",
                  paddingTop: "56.25%",
                  bgcolor: "black",
                }}
              >
                {propertyData.youtube_link && (
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${
                      propertyData.youtube_link.includes("youtu.be/")
                        ? propertyData.youtube_link
                            .split("youtu.be/")[1]
                            ?.split("?")[0] || ""
                        : propertyData.youtube_link
                            .split("v=")[1]
                            ?.split("&")[0] || ""
                    }?autoplay=1`}
                    title="Property Video Tour"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                  />
                )}
              </Box>
            </Dialog>

            {/* Sticky Navigation Tabs */}
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                mt: 4,
                flexWrap: "wrap",
                position: "sticky",
                top: 80,
                zIndex: 10,
                background: "white",
                py: 2,
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              {sectionTabs
                .filter((tab) => {
                  if (propertyData.isPlot) {
                    return ["Overview", "Gallery", "EMI Calculator"].includes(
                      tab,
                    );
                  }
                  return true;
                })
                .map((label, index) => (
                  <Button
                    key={index}
                    variant="contained"
                    onClick={() => setActiveTab(label)}
                    sx={{
                      textTransform: "none",
                      width: { xs: "calc(50% - 6px)", sm: "150px" },
                      backgroundColor:
                        label === activeTab ? "#FF5722" : "white",
                      color: label === activeTab ? "white" : "#4B5563",
                      boxShadow: "none",
                      border:
                        label === activeTab ? "none" : "1px solid #E5E7EB",
                      borderRadius: "8px",
                      py: 1,
                      fontFamily: "Inter",
                      fontWeight: 600,
                      fontSize: "14px",
                      "&:hover": {
                        backgroundColor:
                          label === activeTab ? "#E64A19" : "#F3F4F6",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {label}
                  </Button>
                ))}
            </Box>

            {/* Tab Content Panels */}
            {activeTab === "Overview" && (
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  bgcolor: "white",
                  borderRadius: 3,
                  border: "1px solid #E5E7EB",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Manrope",
                    fontWeight: 700,
                    mb: 3,
                    color: "#1C1917",
                  }}
                >
                  Overview
                </Typography>
                <Grid container spacing={3}>
                  {overviewFields.map(([label, value], i) => (
                    <Grid item xs={6} sm={4} key={i}>
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontSize: "13px",
                          color: "#6B7280",
                          fontWeight: 500,
                          mb: 0.5,
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontSize: "16px",
                          color: "#1C1917",
                          fontWeight: 700,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {value}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ mb: 3 }} />

                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "Manrope",
                        fontWeight: 700,
                        mb: 2,
                        color: "#1C1917",
                      }}
                    >
                      More about {propertyData.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontSize: "15px",
                        color: "#4B5563",
                        lineHeight: 1.7,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {propertyData.aboutus ||
                        propertyData.aboutUs ||
                        propertyData.description ||
                        propertyData.about ||
                        "No description available."}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "Manrope",
                        fontWeight: 700,
                        mb: 2,
                        color: "#1C1917",
                      }}
                    >
                      Disclaimer
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        color: "#6B7280",
                        lineHeight: 1.7,
                        whiteSpace: "pre-line",
                        fontStyle: "italic",
                      }}
                    >
                      {propertyData.disclaimer
                        ? propertyData.disclaimer.length > 250
                          ? `${propertyData.disclaimer.slice(0, 250)}...`
                          : propertyData.disclaimer
                        : "No disclaimer available."}
                    </Typography>
                    {propertyData.disclaimer &&
                      propertyData.disclaimer.length > 250 && (
                        <Button
                          onClick={() => {
                            const isPlot = propertyData.isPlot
                              ? "&type=plot"
                              : "";
                            window.open(
                              `/disclaimer?project=${propertyData.id}${isPlot}`,
                              "_blank",
                            );
                          }}
                          sx={{
                            textTransform: "none",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#FF5722",
                            mt: 1,
                            p: 0,
                            minWidth: 0,
                            "&:hover": {
                              color: "#E64A19",
                              bgcolor: "transparent",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Read Full Disclaimer
                        </Button>
                      )}
                  </Box>
                </Box>
              </Box>
            )}

            {activeTab === "Floor Plan" && (
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  bgcolor: "white",
                  borderRadius: 3,
                  border: "1px solid #E5E7EB",
                }}
              >
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontFamily: "Manrope", fontWeight: 700, mb: 2 }}
                  >
                    Floor Plans
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    {bhkTypes.map((bhk) => (
                      <Box
                        key={bhk}
                        onClick={() => setActiveBhkTab(bhk)}
                        sx={{
                          py: 1.5,
                          px: 3,
                          cursor: "pointer",
                          borderBottom:
                            activeBhkTab === bhk ? "3px solid" : "none",
                          borderColor: "#B02F00",
                          color: activeBhkTab === bhk ? "#B02F00" : "#6B7280",
                          fontWeight: activeBhkTab === bhk ? 700 : 500,
                          fontSize: "15px",
                          fontFamily: "Inter",
                          transition: "all 0.2s",
                          "&:hover": {
                            color: "#B02F00",
                          },
                        }}
                      >
                        {bhk}
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <Box
                    sx={{
                      minWidth: "600px",
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    {/* Header */}
                    <Typography
                      sx={{
                        fontWeight: 700,
                        py: 1.5,
                        borderBottom: "2px solid #E5E7EB",
                        color: "#374151",
                        fontFamily: "Inter",
                        fontSize: "14px",
                      }}
                    >
                      Floor Plan
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        py: 1.5,
                        borderBottom: "2px solid #E5E7EB",
                        color: "#374151",
                        fontFamily: "Inter",
                        fontSize: "14px",
                      }}
                    >
                      Area
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        py: 1.5,
                        borderBottom: "2px solid #E5E7EB",
                        color: "#374151",
                        fontFamily: "Inter",
                        fontSize: "14px",
                      }}
                    >
                      Price
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        py: 1.5,
                        borderBottom: "2px solid #E5E7EB",
                        color: "#374151",
                        fontFamily: "Inter",
                        fontSize: "14px",
                      }}
                    >
                      Enquire
                    </Typography>

                    {/* Table Rows */}
                    {filteredFloorPlans.length > 0 ? (
                      filteredFloorPlans.map((plan, idx) => (
                        <React.Fragment key={idx}>
                          <Box
                            sx={{ py: 1.5, borderBottom: "1px solid #F3F4F6" }}
                          >
                            <Box
                              component="img"
                              src={plan.image}
                              alt={`${plan.bhkType} Floor Plan`}
                              loading="lazy"
                              sx={{
                                width: "100px",
                                height: "70px",
                                objectFit: "cover",
                                borderRadius: 1.5,
                                border: "1px solid #E5E7EB",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                "&:hover": { transform: "scale(1.05)" },
                              }}
                              onClick={() =>
                                handleGalleryOpen(
                                  images.findIndex(
                                    (img) => img === plan.image,
                                  ) || 0,
                                )
                              }
                            />
                          </Box>
                          <Box
                            sx={{ py: 1.5, borderBottom: "1px solid #F3F4F6" }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Inter",
                                fontWeight: 700,
                                fontSize: "15px",
                                color: "#111827",
                              }}
                            >
                              {plan.area} sqft
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Inter",
                                fontSize: "12px",
                                color: "#6B7280",
                              }}
                            >
                              {plan.bhkType}
                            </Typography>
                          </Box>
                          <Box
                            sx={{ py: 1.5, borderBottom: "1px solid #F3F4F6" }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Inter",
                                fontWeight: 800,
                                fontSize: "16px",
                                color: "#B02F00",
                              }}
                            >
                              {formatPrice(plan.price)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{ py: 1.5, borderBottom: "1px solid #F3F4F6" }}
                          >
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                fontFamily: "Inter",
                                bgcolor: "#B02F00",
                                borderRadius: "6px",
                                padding: "6px 16px",
                                fontSize: "13px",
                                "&:hover": { backgroundColor: "#8C2500" },
                              }}
                              onClick={() => handleCallbackOpen(plan)}
                            >
                              Enquire Now
                            </Button>
                          </Box>
                        </React.Fragment>
                      ))
                    ) : (
                      <Box
                        sx={{
                          gridColumn: "1 / -1",
                          textAlign: "center",
                          py: 4,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: "Inter", color: "#6B7280" }}
                        >
                          No floor plans available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            {/* Floor Plan Callback Enquiry Dialog */}
            <Dialog
              open={callbackOpen}
              onClose={handleCallbackClose}
              maxWidth="sm"
              fullWidth
              PaperProps={{ sx: { borderRadius: 3 } }}
            >
              <DialogTitle
                sx={{
                  pb: 1,
                  fontFamily: "Manrope",
                  fontWeight: 700,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                Enquire About {selectedFloorPlan?.bhkType}
                <IconButton onClick={handleCallbackClose} size="small">
                  <Close />
                </IconButton>
              </DialogTitle>
              <form onSubmit={handleCallbackSubmit}>
                <DialogContent sx={{ pt: 1 }}>
                  {callbackError && (
                    <Typography
                      color="error"
                      sx={{ mb: 2, fontFamily: "Inter", fontSize: "14px" }}
                    >
                      {callbackError}
                    </Typography>
                  )}
                  {callbackSuccess ? (
                    <Typography
                      color="success.main"
                      sx={{ mb: 2, fontFamily: "Inter", fontWeight: 600 }}
                    >
                      {callbackSuccess}
                    </Typography>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        label="Your Name"
                        name="name"
                        value={callbackForm.name}
                        onChange={handleCallbackChange}
                        margin="dense"
                        required
                        sx={{ mb: 2 }}
                      />

                      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                        <FormControl sx={{ minWidth: 100 }} size="small">
                          <InputLabel>Code</InputLabel>
                          <Select
                            name="countryCode"
                            value={callbackForm.countryCode}
                            onChange={handleCallbackChange}
                            label="Code"
                            size="small"
                          >
                            <MenuItem value="+91">+91 (IN)</MenuItem>
                            <MenuItem value="+1">+1 (US)</MenuItem>
                            <MenuItem value="+44">+44 (UK)</MenuItem>
                            <MenuItem value="+971">+971 (UAE)</MenuItem>
                            <MenuItem value="+65">+65 (SG)</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          fullWidth
                          size="small"
                          label="Phone Number"
                          name="phone"
                          value={callbackForm.phone}
                          onChange={handleCallbackChange}
                          type="tel"
                          inputProps={{ maxLength: 10 }}
                          required
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          mb: 2,
                          fontFamily: "Inter",
                          color: "#6B7280",
                          fontSize: "13px",
                        }}
                      >
                        Our representative will contact you within 30 minutes
                        during business hours (9 AM - 9 PM IST).
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={callbackForm.consent}
                            onChange={(e) =>
                              setCallbackForm({
                                ...callbackForm,
                                consent: e.target.checked,
                              })
                            }
                            required
                          />
                        }
                        label="I agree to be contacted by WhatsApp, SMS, Phone & Email"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.85rem",
                            color: "#374151",
                            fontFamily: "Inter",
                          },
                        }}
                      />
                    </>
                  )}
                </DialogContent>
                {!callbackSuccess && (
                  <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                      onClick={handleCallbackClose}
                      sx={{
                        textTransform: "none",
                        fontFamily: "Inter",
                        color: "#4B5563",
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        textTransform: "none",
                        fontFamily: "Inter",
                        bgcolor: "#B02F00",
                        "&:hover": { bgcolor: "#8C2500" },
                      }}
                    >
                      Submit Enquiry
                    </Button>
                  </DialogActions>
                )}
              </form>
            </Dialog>

            {activeTab === "Amenities" && (
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  bgcolor: "white",
                  borderRadius: 3,
                  border: "1px solid #E5E7EB",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontFamily: "Manrope", fontWeight: 700, mb: 3 }}
                >
                  Amenities
                </Typography>
                <Grid container spacing={3}>
                  {propertyData.propertyAmenities?.map((item, index) => (
                    <Grid item xs={6} sm={3} key={index}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 2,
                          border: "1px solid #F3F4F6",
                          borderRadius: 2,
                          transition: "all 0.2s",
                          "&:hover": {
                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                            borderColor: "#E5E7EB",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "50%",
                            backgroundColor: "#FFF5F2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 12px auto",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            loading="lazy"
                            src={item.Amenity?.image}
                            alt={item.Amenity?.name}
                            style={{
                              width: "60%",
                              height: "60%",
                              objectFit: "contain",
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: "Inter",
                            fontSize: "14px",
                            color: "#374151",
                            fontWeight: 600,
                          }}
                        >
                          {item.Amenity?.name}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {activeTab === "Gallery" && (
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  bgcolor: "white",
                  borderRadius: 3,
                  border: "1px solid #E5E7EB",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontFamily: "Manrope", fontWeight: 700, mb: 2 }}
                >
                  Gallery
                </Typography>

                {/* Gallery Categories */}
                <Box sx={{ borderBottom: 1, borderColor: "#F3F4F6", mb: 3 }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {galleryTypes.map((tab) => (
                      <Box
                        key={tab.type}
                        onClick={() => setActiveGalleryTab(tab.type)}
                        sx={{
                          py: 1.5,
                          px: 3,
                          cursor: "pointer",
                          borderBottom:
                            activeGalleryTab === tab.type
                              ? "3px solid"
                              : "none",
                          borderColor: "#B02F00",
                          color:
                            activeGalleryTab === tab.type
                              ? "#B02F00"
                              : "#6B7280",
                          fontWeight: activeGalleryTab === tab.type ? 700 : 500,
                          fontSize: "14px",
                          fontFamily: "Inter",
                          transition: "all 0.2s",
                          "&:hover": { color: "#B02F00" },
                        }}
                      >
                        {tab.label}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Gallery Content */}
                {activeGalleryTab === "video" ? (
                  <Box sx={{ py: 2 }}>
                    {propertyData?.youtube_link ? (
                      <Box
                        onClick={() => setVideoOpen(true)}
                        sx={{
                          position: "relative",
                          width: { xs: "100%", md: "220px" },
                          height: "130px",
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                          cursor: "pointer",
                          "&:hover": { opacity: 0.95 },
                        }}
                      >
                        <Box
                          component="img"
                          src={`https://img.youtube.com/vi/${
                            propertyData.youtube_link.includes("youtu.be/")
                              ? propertyData.youtube_link
                                  .split("youtu.be/")[1]
                                  ?.split("?")[0] || ""
                              : propertyData.youtube_link
                                  .split("v=")[1]
                                  ?.split("&")[0] || ""
                          }/hqdefault.jpg`}
                          alt="Video Thumbnail"
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "40px",
                            height: "40px",
                            backgroundColor: "rgba(255, 87, 34, 0.9)",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "&:before": {
                              content: '""',
                              width: 0,
                              height: 0,
                              borderTop: "6px solid transparent",
                              borderBottom: "6px solid transparent",
                              borderLeft: "12px solid white",
                              marginLeft: "3px",
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography
                        sx={{ fontFamily: "Inter", color: "#6B7280" }}
                      >
                        No video available
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {filteredGalleryItems.map((gallery, idx) => (
                      <Grid item xs={6} sm={4} key={gallery.id || idx}>
                        <Box
                          component="img"
                          loading="lazy"
                          src={gallery.mediaLink}
                          alt="Gallery item"
                          sx={{
                            width: "100%",
                            height: "160px",
                            borderRadius: 2,
                            objectFit: "cover",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            "&:hover": { transform: "scale(1.02)" },
                          }}
                          onClick={() => {
                            const imgIdx = images.findIndex(
                              (img) => img === gallery.mediaLink,
                            );
                            if (imgIdx !== -1) handleGalleryOpen(imgIdx);
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {activeTab === "EMI Calculator" && (
              <Box sx={{ mt: 4 }}>
                <EMICalculator
                  defaultAmount={propertyData.minPrice || propertyData.price}
                />
              </Box>
            )}
          </Box>

          {/* Right Section: Form Widget */}
          <Box
            sx={{
              flex: { md: 1 },
              width: "100%",
            }}
          >
            <Paper
              sx={{
                p: 3,
                border: "1px solid #E5E7EB",
                borderRadius: 3,
                bgcolor: "white",
                position: { xs: "static", md: "sticky" },
                top: "120px",
                zIndex: 5,
              }}
              elevation={0}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Manrope",
                  fontWeight: 700,
                  mb: 2,
                  fontSize: "18px",
                  color: "#1C1917",
                  lineHeight: 1.3,
                }}
              >
                Interested to buy property in {propertyData.name}?
              </Typography>

              <form onSubmit={handleEnquirySubmit}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name"
                  margin="normal"
                  name="name"
                  value={enquiryForm.name}
                  onChange={handleEnquiryChange}
                  required
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Mobile Number"
                  margin="normal"
                  name="mobile"
                  value={enquiryForm.mobile}
                  onChange={handleEnquiryChange}
                  inputProps={{ maxLength: 10, pattern: "[0-9]{10}" }}
                  required
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      name="agree"
                      checked={enquiryForm.agree}
                      onChange={handleEnquiryChange}
                    />
                  }
                  label="I agree to be contacted by WhatsApp, SMS, Phone & Email"
                  sx={{
                    mt: 1,
                    mb: 1,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "12px",
                      color: "#4B5563",
                      fontFamily: "Inter",
                      fontWeight: 500,
                      lineHeight: 1.3,
                    },
                  }}
                />

                {enquiryError && (
                  <Typography
                    color="error"
                    sx={{ mt: 1, fontSize: "13px", fontFamily: "Inter" }}
                  >
                    {enquiryError}
                  </Typography>
                )}
                {enquirySuccess && (
                  <Typography
                    color="success.main"
                    sx={{
                      mt: 1,
                      fontSize: "13px",
                      fontFamily: "Inter",
                      fontWeight: 600,
                    }}
                  >
                    {enquirySuccess}
                  </Typography>
                )}

                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  sx={{
                    mt: 2,
                    bgcolor: "#FF5722",
                    fontFamily: "Inter",
                    fontWeight: 600,
                    textTransform: "none",
                    py: 1.2,
                    borderRadius: 2,
                    fontSize: "15px",
                    "&:hover": { bgcolor: "#E64A19" },
                  }}
                >
                  GET CALL BACK
                </Button>

                {/* Assured Callbacks Branding & Info */}
                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "#FFF5F2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <AccessTimeIcon sx={{ color: "#FF5722", fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 700,
                          color: "#1F2937",
                        }}
                      >
                        Assured Callback in 5 mins
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Inter",
                          color: "#6B7280",
                          fontSize: "12px",
                          mt: 0.5,
                        }}
                      >
                        Get an assured callback in 5 mins from sales expert (24
                        Hrs)
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "#FFF5F2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <WorkspacePremiumIcon
                        sx={{ color: "#FF5722", fontSize: 22 }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 700,
                          color: "#1F2937",
                        }}
                      >
                        Authorised Channel Partner
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Inter",
                          color: "#6B7280",
                          fontSize: "12px",
                          mt: 0.5,
                        }}
                      >
                        Abilio Realty is an authorised channel partner for this
                        project
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </form>
            </Paper>
          </Box>
        </Box>
      </Container>

      {/* Brochure Form Modal */}
      <Dialog
        open={brochureModalOpen}
        onClose={handleBrochureModalClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontFamily: "Manrope", fontWeight: 700, color: "#FF5722" }}
            >
              Download Brochure
            </Typography>
            <IconButton onClick={handleBrochureModalClose}>
              <Close />
            </IconButton>
          </Box>

          <form onSubmit={handleBrochureSubmit}>
            <Typography
              variant="body2"
              sx={{ mb: 2, fontFamily: "Inter", color: "#4B5563" }}
            >
              Please provide your details to download the brochure:
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Full Name"
              margin="normal"
              name="name"
              value={brochureForm.name}
              onChange={handleBrochureFormChange}
              required
            />
            <TextField
              fullWidth
              size="small"
              label="Mobile Number"
              margin="normal"
              name="mobile"
              value={brochureForm.mobile}
              onChange={handleBrochureFormChange}
              inputProps={{ maxLength: 10, pattern: "[0-9]{10}" }}
              required
            />
            <TextField
              fullWidth
              size="small"
              label="Email Address"
              margin="normal"
              name="email"
              type="email"
              value={brochureForm.email}
              onChange={handleBrochureFormChange}
              required
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="agree"
                  checked={brochureForm.agree}
                  onChange={handleBrochureFormChange}
                />
              }
              label="I agree to be contacted by WhatsApp, SMS, Phone & Email"
              sx={{
                mt: 1,
                "& .MuiFormControlLabel-label": {
                  fontSize: "12px",
                  color: "#4B5563",
                  fontFamily: "Inter",
                },
              }}
            />

            {brochureError && (
              <Typography
                color="error"
                sx={{ mt: 1, fontSize: "13px", fontFamily: "Inter" }}
              >
                {brochureError}
              </Typography>
            )}
            {brochureSuccess && (
              <Typography
                color="success.main"
                sx={{
                  mt: 1,
                  fontSize: "13px",
                  fontFamily: "Inter",
                  fontWeight: 600,
                }}
              >
                {brochureSuccess}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleBrochureModalClose}
                sx={{ flex: 1, textTransform: "none", fontFamily: "Inter" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  flex: 1,
                  textTransform: "none",
                  fontFamily: "Inter",
                  bgcolor: "#FF5722",
                  "&:hover": { bgcolor: "#E64A19" },
                }}
                startIcon={<i className="fas fa-download"></i>}
              >
                Download Brochure
              </Button>
            </Box>
          </form>
        </Box>
      </Dialog>

      {/* Lightbox / Fullscreen Gallery Dialog */}
      <Dialog
        open={galleryOpen}
        onClose={handleGalleryClose}
        fullScreen
        PaperProps={{
          style: {
            backgroundColor: "#000",
            color: "#fff",
            margin: 0,
            maxWidth: "100%",
            maxHeight: "100%",
            overflow: "hidden",
          },
        }}
      >
        <DialogContent
          sx={{
            position: "relative",
            p: 0,
            m: 0,
            width: "100%",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#000",
            overflow: "hidden",
            "&.MuiDialogContent-root": { padding: 0 },
          }}
        >
          <IconButton
            onClick={handleGalleryClose}
            sx={{
              position: "fixed",
              top: 16,
              right: 16,
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 10,
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
            }}
          >
            <Close />
          </IconButton>

          {images.length > 1 && (
            <>
              <IconButton
                onClick={handleGalleryPrev}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: 16,
                  transform: "translateY(-50%)",
                  zIndex: 1,
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={handleGalleryNext}
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 16,
                  transform: "translateY(-50%)",
                  zIndex: 1,
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}

          {images[galleryIdx]?.includes("youtube.com") ||
          images[galleryIdx]?.includes("youtu.be") ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <iframe
                width="80%"
                height="80%"
                src={`https://www.youtube.com/embed/${
                  images[galleryIdx].includes("youtu.be/")
                    ? images[galleryIdx].split("youtu.be/")[1]?.split("?")[0] ||
                      ""
                    : images[galleryIdx].split("v=")[1]?.split("&")[0] || ""
                }?autoplay=1`}
                title="Property Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  borderRadius: "8px",
                  maxWidth: "800px",
                  maxHeight: "450px",
                  aspectRatio: "16/9",
                }}
              />
            </Box>
          ) : (
            <Box
              component="img"
              src={images[galleryIdx]}
              alt={`Gallery image ${galleryIdx + 1}`}
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: "85vh",
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
          )}

          <Box
            sx={{
              position: "fixed",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              color: "white",
              px: 2,
              py: 0.8,
              borderRadius: 20,
              fontSize: "14px",
              fontFamily: "Inter",
              zIndex: 10,
            }}
          >
            {galleryIdx + 1} / {images.length}
          </Box>
        </DialogContent>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default PropertyDetails;
